import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useSessionSnapshots } from "../../application/useSnapshots";

/**
 * Renders the constellation board snapshots captured during a session. The
 * images are streamed (authenticated) and shown via object URLs, so they load
 * in every environment without relying on URL-signing permissions. Shows
 * nothing when the session has no board or no snapshots.
 */
export default function ConstellationSnapshots({ sessionId }: { sessionId: string }) {
    const { t } = useTranslation();
    const { items } = useSessionSnapshots(sessionId);
    const [active, setActive] = useState<string | null>(null);

    if (items.length === 0) return null;

    return (
        <div className="glass-effect glass-border border rounded-2xl p-8 shadow-theme-sm transition-all hover:shadow-theme-md">
            <h3 className="mb-6 text-lg font-semibold text-gray-800 dark:text-gray-100">
                {t("constellation.snapshots.title", "Constellation snapshots")}
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {items.map((item, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => setActive(item.url)}
                        className="group overflow-hidden rounded-xl border border-gray-100 bg-gray-50 text-left transition-all hover:shadow-theme-md dark:border-white/10 dark:bg-white/5"
                    >
                        <img
                            src={item.url}
                            alt={item.name || t("constellation.snapshots.alt", "Constellation snapshot {{n}}", {
                                n: i + 1,
                            })}
                            className="aspect-square w-full object-cover"
                            loading="lazy"
                        />
                        <span className="block truncate px-2 py-1.5 text-xs font-medium text-content-secondary">
                            {item.name || t("constellation.snapshots.alt", "Constellation snapshot {{n}}", {
                                n: i + 1,
                            })}
                        </span>
                    </button>
                ))}
            </div>

            {active && createPortal(
                <div
                    className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setActive(null)}
                    role="dialog"
                    aria-modal="true"
                >
                    <img
                        src={active}
                        alt={t("constellation.snapshots.title", "Constellation snapshots")}
                        className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
                    />
                </div>,
                document.body,
            )}
        </div>
    );
}
